import {
  Flex,
  Table,
  type TableColumnType,
  TablePageSizeSelector,
  Text,
  theme,
} from '@/components';
import { BigCalculator, formatValueByUnit } from '@/utils';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import { useFundSheetAnalysisPageSearchQueryStates } from '../_hooks/search-query-states';
import type { KsAnalysisRow } from '../_models/ks-analysis';
import { buildSortUpdate } from '../_utils/build-table-state-updates';
import {
  type KsTableRow,
  getKsTableDataSource,
} from '../_utils/get-ks-table-datasource';
import {
  compareByKsSortKeys,
  getKsActiveChartYear,
  getKsColumnSortDirections,
  getKsColumnSortOrder,
  resolveKsSortKeys,
} from '../_utils/resolve-ks-ordering';
import { KsAnalysisDrillThroughDropdown } from './KsAnalysisDrillThroughDropdown';

type Props = {
  data: KsAnalysisRow[];
};

const renderText = (value: number | undefined | null) => {
  if (value === undefined || value === null) {
    return '';
  }
  return formatValueByUnit(value, {
    operation: 'round',
    unit: 'thousand',
  });
};

export const KsAnalysisResultTable: FC<Props> = ({ data }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();

  const {
    searchQueryStates: {
      page,
      pageSize,
      order,
      chartYear,
      analysisAxis,
      graphType,
    },
    setSearchQueryStates,
  } = useFundSheetAnalysisPageSearchQueryStates();

  const fiscalYears = useMemo(
    () => [...new Set(data.map((item) => item.fiscalYear))].sort(),
    [data],
  );

  // 初期表示は graphType 依存: 積み上げ棒=前年度降順→当年度降順、円/ツリー=選択年度降順 (RS-4487)。
  // 列ヘッダクリック時は order(URL) の単一キーに従う。凡例(KsAnalysisVisualizer)・列ヘッダの矢印と同期。
  const sortKeys = useMemo(
    () =>
      resolveKsSortKeys(order, fiscalYears, analysisAxis, graphType, chartYear),
    [order, fiscalYears, analysisAxis, graphType, chartYear],
  );

  // 円/ツリー(organization軸を除く)は order 未指定でも実際には chartYear 基準の年度で
  // 単一キーソートされている。「並びは変わっているのに矢印が付かない」という見た目の
  // 不整合を防ぐため、そのアクティブ年度を列ヘッダの矢印表示にもフォールバックとして渡す (RS-4491)。
  const isPieOrTreeMapGraph =
    graphType === 'pieChart' || graphType === 'treeMap';
  const activeYearForColumnArrow =
    isPieOrTreeMapGraph && analysisAxis !== 'organization'
      ? getKsActiveChartYear(chartYear, fiscalYears)
      : null;

  const tableDataSource = useMemo(() => {
    const rows = getKsTableDataSource({ data, analysisAxis });
    if (sortKeys.length === 0) {
      return rows;
    }
    return [...rows].sort(
      compareByKsSortKeys(sortKeys, {
        getTotal: (row, year) =>
          row.fiscalYears.find((item) => item.year === year)?.total,
        getName: (row) => row.displayName,
      }),
    );
  }, [data, analysisAxis, sortKeys]);

  return (
    <>
      <Flex justify="flex-end" gap={16} style={{ marginBottom: 16 }}>
        <TablePageSizeSelector
          defaultPageSize={pageSize}
          onChange={(pageSize) => {
            setSearchQueryStates((prev) => ({
              ...prev,
              page: 1,
              pageSize,
            }));
          }}
        />
        <KsAnalysisDrillThroughDropdown fiscalYears={fiscalYears} />
      </Flex>
      <Table
        rowKey={(row) => row.id}
        columns={[
          {
            title: t('thousand-yen-unit-label'),
            key: 'name',
            width: 400,
            render: (_, record) => (
              <Text maxWidth={400} text={record.displayName} />
            ),
          },
          ...(fiscalYears.map((fiscalYear) => ({
            title: `${fiscalYear}`,
            dataIndex: fiscalYear,
            key: fiscalYear,
            width: 250,
            align: 'right',
            // 制御プロップにすることで、年度タブ選択やグラフ種別切替で order(URL) が
            // 変わった際も列ヘッダの矢印・Table内部ソート状態が追従する (RS-4487)。
            // order(URL) の指定を優先し、無ければ円/ツリーのアクティブ年度(activeYearForColumnArrow)に
            // フォールバックする。積み上げ棒・organization軸では従来どおりフォールバックしない (RS-4490 / RS-4491)。
            sortOrder: getKsColumnSortOrder(
              order,
              fiscalYear,
              activeYearForColumnArrow,
            ),
            // アクティブ年度列は▽フォールバック表示があるため降順始まりのサイクルに反転し、
            // 「降順→解除→フォールバックで再び降順」のループで昇順に到達できなくなるのを防ぐ (RS-4491)。
            sortDirections: getKsColumnSortDirections(
              fiscalYear,
              activeYearForColumnArrow,
            ),
            // `dataSource`(tableDataSource) は既に compareByKsSortKeys で
            // 未入力(空欄)を末尾固定した順序にソート済み。ここで AntD 標準の比較関数を
            // 渡すと、sortOrder 有効時に AntD が `?? 0` 相当の単純比較で dataSource を
            // 再ソートしてしまい、未入力の末尾固定が崩れる (RS-4525)。
            // `sorter: true` にして AntD 側の自動再ソートを無効化し、事前ソート済みの
            // dataSource をそのまま表示する。
            sorter: true,
            render: (_, record) => {
              const total = record.fiscalYears.find(
                (item) => item.year === fiscalYear,
              )?.total;
              return renderText(total);
            },
          })) satisfies Array<TableColumnType<KsTableRow>>),
        ]}
        dataSource={tableDataSource}
        pagination={{
          current: page,
          pageSize,
          showSizeChanger: false,
          total: tableDataSource.length,
          onChange: (page) => {
            setSearchQueryStates((prev) => ({ ...prev, page }));
          },
        }}
        onChange={(_, __, sorter, extra) => {
          // ページング操作時も onChange が発火するため、ソート操作のみ反映する。
          // （そうしないと buildSortUpdate が page を 1 に戻し、ページ遷移が無効化される）
          if (extra.action !== 'sort') {
            return;
          }
          invariant(!Array.isArray(sorter), t('unreachable-code'));
          setSearchQueryStates((prev) => ({
            ...prev,
            ...buildSortUpdate(sorter),
          }));
        }}
        summary={
          // 軸が null（合計のみ）の場合は単一行が既に合計なので summary を出さない
          analysisAxis === null
            ? undefined
            : () => (
                <Table.Summary.Row
                  style={{
                    background: token.colorBgLayout,
                    fontWeight: 600,
                  }}
                >
                  <Table.Summary.Cell index={0}>
                    {t('total')}
                  </Table.Summary.Cell>
                  {fiscalYears.map((year, index) => {
                    // BE は pagination なしで全件返すため、表示中のページに関係なく
                    // tableDataSource (=全行) を集計対象にする。
                    const total = tableDataSource.reduce((acc, curr) => {
                      return BigCalculator.add(
                        acc,
                        curr.fiscalYears.find((item) => item.year === year)
                          ?.total ?? 0,
                      );
                    }, 0);
                    return (
                      <Table.Summary.Cell
                        key={year}
                        index={index + 1}
                        align="right"
                      >
                        {renderText(total)}
                      </Table.Summary.Cell>
                    );
                  })}
                </Table.Summary.Row>
              )
        }
      />
    </>
  );
};
